/**
 * ReferralProgress (Jest client)
 *
 * Referral dashboard for the Jest distribution. Unlike the web app there is no
 * shareable /join URL — the Jest platform owns the invite loop:
 *
 *   1. "Invite Friends" → JestSDK.referrals.shareReferralLink() opens the
 *      platform share dialog.
 *   2. Conversions come back via JestSDK.referrals.listReferrals() as a
 *      platform-signed JWS (`referralsSigned`).
 *   3. On mount we forward that JWS to POST /api/jest/referrals/sync, which
 *      verifies it server-side, links referredByUserId, and retro-fires any
 *      rewards the referred player already qualified for.
 *   4. Stats then load from GET /api/rewards/referral-stats as on web.
 *
 * Outside the Jest iframe (plain Vite dev) the SDK namespace is missing; the
 * widget degrades to stats-only and the sync is skipped silently.
 */

import { useEffect, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Coin } from '@/components/ui/coin';
import { authFetch } from '@/lib/auth-client';

/** Campaign reference recorded against every invite from this widget. */
const REFERRAL_REFERENCE = 'referral_rewards_v1';

interface ReferralStats {
  referralCode: string;
  totalReferred: number;
  totalQualified: number;
  totalCoinsEarned: number;
  /** Paid to the invitee, not to this user — quoted so the share copy stays honest. */
  welcomeBonusCoins: number;
  recentEvents: Array<{
    referredUsername: string;
    eventType: 'FIRST_EVENT_ENTRY' | 'COIN_PURCHASE';
    coinsAwarded: number;
    createdAt: string;
  }>;
}

const EVENT_LABELS: Record<ReferralStats['recentEvents'][number]['eventType'], string> = {
  FIRST_EVENT_ENTRY: 'Entered first event',
  COIN_PURCHASE: 'Bought coins',
};

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getReferralsApi(): JestSDKReferralsApi | null {
  if (typeof window === 'undefined') return null;
  return window.JestSDK?.referrals ?? null;
}

/**
 * Pull the platform-signed conversion list and let the backend verify, link,
 * and retro-credit. Best-effort: any failure leaves stats as-is.
 */
async function syncReferralConversions(): Promise<void> {
  const referrals = getReferralsApi();
  if (!referrals) return;
  try {
    const { referralsSigned } = await referrals.listReferrals();
    if (!referralsSigned) return;
    await authFetch('/api/jest/referrals/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ referralsSigned }),
    });
  } catch {
    // Non-fatal — stats still load; next mount retries.
  }
}

export interface ReferralProgressProps {
  /** If true, renders without the outer card wrapper (for embedding in a larger card) */
  borderless?: boolean;
  className?: string;
}

export default function ReferralProgress({ borderless, className }: ReferralProgressProps) {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  const canInvite = getReferralsApi() !== null;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Sync first so retro-credited rewards show up in the stats we render.
      await syncReferralConversions();
      try {
        const res = await authFetch('/api/rewards/referral-stats');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const payload = (data.data ?? data) as ReferralStats;
        if (!cancelled) {
          setStats(payload);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load referral stats');
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleInvite = useCallback(async () => {
    const referrals = getReferralsApi();
    if (!referrals || sharing) return;
    setSharing(true);
    try {
      // Lead with what the invitee gets — that is the reason to tap. The amount
      // comes from the API rather than a local constant so it tracks whatever
      // admins have the reward set to. Falls back to the old copy if the bonus
      // is disabled, rather than promising zero coins.
      const bonus = stats?.welcomeBonusCoins ?? 0;
      await referrals.shareReferralLink({
        reference: REFERRAL_REFERENCE,
        shareTitle: 'FantasyPRIME',
        shareText: bonus > 0
          ? `Use my link to join FantasyPRIME and start with ${bonus.toLocaleString()} bonus coins.`
          : 'Join me on FantasyPRIME — boost your props and win big.',
      });
    } catch {
      // INVALID_ARGUMENTS or platform errors — nothing actionable for the user here.
    } finally {
      setSharing(false);
    }
  }, [sharing, stats]);

  const wrapperClass = cn(
    borderless ? '' : 'rounded-lg border border-surface-3 bg-surface-2',
    'p-3 space-y-2',
    className,
  );

  if (loading) {
    return (
      <div className={wrapperClass} aria-busy="true">
        <div className="flex items-center gap-1.5">
          <UsersIcon className="w-3.5 h-3.5 text-gold" />
          <span className="text-xs font-bold text-cream">Referrals</span>
        </div>
        <div className="h-16 bg-surface-3/30 rounded animate-pulse" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className={wrapperClass}>
        <div className="flex items-center gap-1.5">
          <UsersIcon className="w-3.5 h-3.5 text-gold" />
          <span className="text-xs font-bold text-cream">Referrals</span>
        </div>
        <p className="text-[11px] text-warm-gray/50">Unable to load referral data.</p>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <UsersIcon className="w-3.5 h-3.5 text-gold" />
          <span className="text-xs font-bold text-cream">Referrals</span>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="text-warm-gray/50">
            {stats.totalQualified}/{stats.totalReferred} qualified
          </span>
          <Coin amount={stats.totalCoinsEarned} size="sm" className="text-gold font-bold" />
        </div>
      </div>

      {/* Explainer — both sides, because what the friend gets is the pitch */}
      <p className="text-[10px] text-warm-gray/40">
        200 coins when a friend enters an event &middot; 300 coins when they buy coins
      </p>
      {stats.welcomeBonusCoins > 0 && (
        <p className="text-[10px] text-gold/70">
          They get {stats.welcomeBonusCoins.toLocaleString()} bonus coins for using your link
        </p>
      )}

      {/* Invite via the Jest platform share dialog */}
      {canInvite ? (
        <button
          type="button"
          onClick={handleInvite}
          disabled={sharing}
          className={cn(
            'w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all border',
            sharing
              ? 'bg-surface-3 text-warm-gray/50 border-surface-4 cursor-wait'
              : 'bg-gold/15 text-gold hover:bg-gold/25 border-gold/30',
          )}
        >
          <ShareIcon className="w-3.5 h-3.5" />
          {sharing ? 'Opening share dialog…' : 'Invite Friends'}
        </button>
      ) : (
        <p className="text-[10px] text-warm-gray/40 italic">
          Inviting friends is available when playing on Jest.
        </p>
      )}

      {/* Recent activity */}
      {stats.recentEvents.length > 0 && (
        <div className="space-y-0.5 pt-1 border-t border-surface-3/50">
          {stats.recentEvents.slice(0, 5).map((event, i) => (
            <div key={i} className="flex items-center justify-between text-[10px] py-0.5">
              <span className="text-cream/60 truncate">
                <span className="font-medium">{event.referredUsername}</span>
                <span className="text-warm-gray/40 ml-1">{EVENT_LABELS[event.eventType]}</span>
              </span>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                <span className="text-gold font-bold">+{event.coinsAwarded}</span>
                <span className="text-warm-gray/30">{formatRelativeTime(event.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {stats.totalReferred === 0 && (
        <div className="pt-1 border-t border-surface-3/50">
          <p className="text-[10px] text-warm-gray/40 italic">No referrals yet — invite friends to start earning.</p>
        </div>
      )}
    </div>
  );
}
